/**
 * Global Error Handler Utility
 *
 * Provides a centralized way to handle errors throughout the application.
 * Features:
 * - Shows toast notifications for user feedback
 * - Reports errors to server for monitoring
 * - Works with existing ToastContext and AuthContext
 */

import { getApiEndpoint } from "../config/config";

// Error severity levels
export const ERROR_LEVELS = {
  LOW: "low",
  MEDIUM: "medium",
  HIGH: "high",
  CRITICAL: "critical",
};

// Error types for categorization
export const ERROR_TYPES = {
  API: "api",
  NETWORK: "network",
  VALIDATION: "validation",
  AUTHENTICATION: "authentication",
  PERMISSION: "permission",
  UNKNOWN: "unknown",
};

/**
 * Global error handler class
 */
class ErrorHandler {
  constructor() {
    this.toastContext = null;
    this.authContext = null;
    this.isReporting = false; // Prevent infinite loops
  }

  /**
   * Initialize the error handler with required contexts
   * This should be called once when the app starts
   */
  initialize(toastContext, authContext) {
    this.toastContext = toastContext;
    this.authContext = authContext;
  }

  /**
   * Get device information for error reporting
   */
  getDeviceInfo() {
    const userAgent = navigator.userAgent;
    const platform = navigator.platform;
    const language = navigator.language;
    const screenResolution = `${window.screen.width}x${window.screen.height}`;
    const viewport = `${window.innerWidth}x${window.innerHeight}`;

    return `${userAgent} | Platform: ${platform} | Lang: ${language} | Screen: ${screenResolution} | Viewport: ${viewport}`;
  }

  /**
   * Report error to server
   */
  async reportToServer(
    error,
    message,
    level = ERROR_LEVELS.MEDIUM,
    type = ERROR_TYPES.UNKNOWN
  ) {
    // Prevent infinite loops if error reporting itself fails
    if (this.isReporting) {
      return;
    }

    try {
      this.isReporting = true;

      // Get user info if available
      const user = this.authContext?.user;

      // Prepare error payload
      const errorPayload = {
        rep_id: user?.repId || null,
        device: this.getDeviceInfo(),
        error: this.formatErrorForServer(error, message, level, type),
      };

      // Send to server
      await fetch(getApiEndpoint("/v2/site/error"), {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify(errorPayload),
      });
    } finally {
      this.isReporting = false;
    }
  }

  /**
   * Format error information for server logging
   */
  formatErrorForServer(error, message, level, type) {
    const timestamp = new Date().toISOString();
    const url = window.location.href;
    const userAgent = navigator.userAgent;

    let errorDetails = "";

    if (error instanceof Error) {
      errorDetails = `${error.name}: ${error.message}`;
      if (error.stack) {
        errorDetails += `\nStack: ${error.stack}`;
      }
    } else if (typeof error === "string") {
      errorDetails = error;
    } else {
      errorDetails = JSON.stringify(error);
    }

    return `[${level.toUpperCase()}][${type.toUpperCase()}] ${message}
Time: ${timestamp}
URL: ${url}
User Agent: ${userAgent}
Error Details: ${errorDetails}`;
  }

  /**
   * Main error handling function
   * @param {Error|string|object} error - The error object, message, or data
   * @param {string} userMessage - User-friendly message to show in toast
   * @param {object} options - Additional options
   * @param {string} options.level - Error severity level
   * @param {string} options.type - Error type for categorization
   * @param {boolean} options.showToast - Whether to show toast notification (default: true)
   * @param {boolean} options.reportToServer - Whether to report to server (default: true)
   * @param {number} options.toastDuration - Toast duration in ms (default: 5000)
   */
  handle(error, userMessage, options = {}) {
    const {
      level = ERROR_LEVELS.MEDIUM,
      type = ERROR_TYPES.UNKNOWN,
      showToast = true,
      reportToServer = true,
      toastDuration = 5000,
    } = options;

    // The API may return `message` as an array of validation errors. Join them
    // onto separate lines so each one is readable.
    const displayMessage = Array.isArray(userMessage)
      ? userMessage.join("\n")
      : userMessage;

    // Show toast notification
    if (showToast && this.toastContext?.showError) {
      this.toastContext.showError(displayMessage, toastDuration);
    }

    // Report to server (async, don't wait)
    if (reportToServer && type !== ERROR_TYPES.API) {
      this.reportToServer(error, displayMessage, level, type);
    }
  }

  /**
   * Convenience methods for different error types
   */

  // API errors (network requests, server errors)
  handleApiError(
    error,
    userMessage = "An error occurred while communicating with the server",
    options = {}
  ) {
    this.handle(error, userMessage, {
      ...options,
      type: ERROR_TYPES.API,
      level: options.level || ERROR_LEVELS.MEDIUM,
    });
  }

  // Network errors (no connection, timeout, etc.)
  handleNetworkError(
    error,
    userMessage = "Network error. Please check your connection and try again.",
    options = {}
  ) {
    this.handle(error, userMessage, {
      ...options,
      type: ERROR_TYPES.NETWORK,
      level: options.level || ERROR_LEVELS.HIGH,
    });
  }

  // Validation errors (form validation, input errors)
  handleValidationError(error, userMessage, options = {}) {
    this.handle(error, userMessage, {
      ...options,
      type: ERROR_TYPES.VALIDATION,
      level: options.level || ERROR_LEVELS.LOW,
    });
  }

  // Authentication errors (login, token expires, etc.)
  handleAuthError(
    error,
    userMessage = "Authentication error. Please log in again.",
    options = {}
  ) {
    this.handle(error, userMessage, {
      ...options,
      type: ERROR_TYPES.AUTHENTICATION,
      level: options.level || ERROR_LEVELS.HIGH,
    });
  }

  // Permission errors (unauthorized access)
  handlePermissionError(
    error,
    userMessage = "You do not have permission to perform this action.",
    options = {}
  ) {
    this.handle(error, userMessage, {
      ...options,
      type: ERROR_TYPES.PERMISSION,
      level: options.level || ERROR_LEVELS.MEDIUM,
    });
  }

  // Critical errors (app crashes, critical failures)
  handleCriticalError(
    error,
    userMessage = "A critical error occurred. Please refresh the page.",
    options = {}
  ) {
    this.handle(error, userMessage, {
      ...options,
      type: ERROR_TYPES.UNKNOWN,
      level: ERROR_LEVELS.CRITICAL,
    });
  }

  // Generic error handler with standard message
  handleGenericError(
    error,
    userMessage = "An unexpected error occurred",
    options = {}
  ) {
    this.handle(error, userMessage, {
      ...options,
      type: options.type || ERROR_TYPES.UNKNOWN,
      level: options.level || ERROR_LEVELS.MEDIUM,
    });
  }

  /**
   * Helper to wrap async functions with error handling
   */
  wrapAsync(asyncFunction, errorMessage = "An unexpected error occurred") {
    return async (...args) => {
      try {
        return await asyncFunction(...args);
      } catch (error) {
        this.handle(error, errorMessage);
        throw error; // Re-throw so calling code can handle if needed
      }
    };
  }

  /**
   * Helper to handle fetch response errors
   */
  async handleFetchError(response, customMessage = null) {
    let userMessage =
      customMessage || "An error occurred while processing your request";
    let errorDetails = `HTTP ${response.status} - ${response.statusText}`;

    try {
      // Try to get error details from response
      const contentType = response.headers.get("content-type");
      if (contentType && contentType.includes("application/json")) {
        const errorData = await response.json();
        if (errorData.message) {
          userMessage = errorData.message;
        }
        errorDetails += ` | Server message: ${JSON.stringify(errorData)}`;
      }
    } catch (parseError) {
      // If we can't parse the error response, continue with basic error
      errorDetails += ` | Parse error: ${parseError.message}`;
    }

    // Determine error type based on status code
    let type = ERROR_TYPES.API;
    let level = ERROR_LEVELS.MEDIUM;

    if (response.status >= 400 && response.status < 500) {
      if (response.status === 401) {
        type = ERROR_TYPES.AUTHENTICATION;
        level = ERROR_LEVELS.HIGH;
      } else if (response.status === 403) {
        type = ERROR_TYPES.PERMISSION;
        level = ERROR_LEVELS.MEDIUM;
      } else {
        type = ERROR_TYPES.VALIDATION;
        level = ERROR_LEVELS.LOW;
      }
    } else if (response.status >= 500) {
      level = ERROR_LEVELS.HIGH;
    }

    this.handle(new Error(errorDetails), userMessage, { type, level });
  }
}

// Create and export singleton instance
export const errorHandler = new ErrorHandler();

// Export convenience function for easy importing
export const handleError = (error, message, options) => {
  errorHandler.handle(error, message, options);
};

// Export other convenience functions
export const handleApiError = (error, message, options) => {
  errorHandler.handleApiError(error, message, options);
};

export const handleNetworkError = (error, message, options) => {
  errorHandler.handleNetworkError(error, message, options);
};

export const handleValidationError = (error, message, options) => {
  errorHandler.handleValidationError(error, message, options);
};

export const handleAuthError = (error, message, options) => {
  errorHandler.handleAuthError(error, message, options);
};

export const handlePermissionError = (error, message, options) => {
  errorHandler.handlePermissionError(error, message, options);
};

export const handleCriticalError = (error, message, options) => {
  errorHandler.handleCriticalError(error, message, options);
};

export const handleGenericError = (error, options) => {
  errorHandler.handleGenericError(error, options);
};

export default errorHandler;
